import { httpService } from "./httpService.service";

/**
 * The lifecycle transitions that carry a site visit from planning to completed.
 *
 * These are the same four endpoints the phase screens call — the reporting list's
 * "Submit to approve", the approval screen's approve/reject, and the approved
 * list's "mark as completed". They are collected here so the Site Visit Summary
 * can drive the whole process from one row without duplicating the calls, and so
 * there is one place to look when a transition changes.
 *
 * Approval status: 1 = pending (submitted), 2 = approved, 3 = rejected.
 * Site visit status 6 = visit completed.
 */

const putStatus = (url) =>
  new Promise((resolve, reject) => {
    httpService.put(url, "", resolve, reject);
  });

const requireId = (siteVisitId) => {
  if (!siteVisitId) throw new Error("Missing site visit id.");
};

const changeApprovalStatus = (siteVisitId, approvalStatus, extra = "") => {
  requireId(siteVisitId);
  return putStatus(
    `/api/Site/changeSiteVisitApprovalStatus?SiteVisitId=${siteVisitId}` +
      `&ApprovalStatus=${approvalStatus}${extra}`
  );
};

/** Hands the field report to whoever approves it. */
export const submitSiteVisitForApproval = (siteVisitId) =>
  changeApprovalStatus(siteVisitId, 1);

export const approveSiteVisit = (siteVisitId) =>
  changeApprovalStatus(siteVisitId, 2);

// A rejection always carries a reason — the approval screen makes it a required
// field, so the same rule holds here.
export const rejectSiteVisit = (siteVisitId, rejectionReason) => {
  if (!rejectionReason?.trim()) {
    return Promise.reject(new Error("A rejection reason is required."));
  }
  return changeApprovalStatus(
    siteVisitId,
    3,
    `&RejectionReason=${encodeURIComponent(rejectionReason.trim())}`
  );
};

/** The last step: the visit itself is done. */
export const markSiteVisitCompleted = (siteVisitId) => {
  requireId(siteVisitId);
  return putStatus(
    `/api/Site/changeSiteVisitStatus?SiteVisitId=${siteVisitId}&SiteVisitStatus=6`
  );
};

/**
 * The step a site visit is waiting on, derived from where it actually is.
 *
 * Returned as `{ key, label, tooltip }` so a row can offer one obvious primary
 * action instead of making someone work out which of six things applies. Order
 * matters: the first unmet precondition wins.
 */
export const APPROVAL_STATUS = { pending: 1, approved: 2, rejected: 3 };

export const nextSiteVisitStep = (record) => {
  const hasTracking = !!record?.tracking?.id;
  const approval = record?.approvalStatus;
  const completed = record?.siteVisitStatus === 6;

  if (completed) {
    return { key: "done", label: "Completed", tooltip: "This site visit is complete." };
  }
  if (!hasTracking) {
    return {
      key: "addTracking",
      label: "Add tracking",
      tooltip:
        "This visit has no tracking record yet — add one to start implementation.",
    };
  }
  if (approval === APPROVAL_STATUS.approved) {
    return {
      key: "complete",
      label: "Mark completed",
      tooltip: "The report is approved. Close the site visit out.",
    };
  }
  if (approval === APPROVAL_STATUS.pending) {
    return {
      key: "approve",
      label: "Approve",
      tooltip: "Submitted for approval — approve or reject the field report.",
    };
  }
  if (approval === APPROVAL_STATUS.rejected) {
    return {
      key: "submit",
      label: "Re-submit",
      tooltip:
        `Rejected${record?.rejectionReason ? `: ${record.rejectionReason}` : ""}. ` +
        "Fix the report, then submit it again.",
    };
  }
  return {
    key: "submit",
    label: "Submit",
    tooltip: "Submit this field report for approval.",
  };
};
